import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Text } from '@/design-system/atoms/Text';
import { useBreakpoint } from '@/hooks/useBreakpoint';

import { useSiteSearchContext } from '../../contexts/SiteSearchContext';
import { useSiteSearchState } from '../../hooks/useSiteSearchState';
import { SearchFilters } from '../SearchFilters';
import { SearchHero } from '../SearchHero';
import styles from './SearchHeader.module.scss';

/**
 * The SearchHeader component contains the search controls
 * for the search results page.
 * @param {object} props - The component props
 * @param {array} props.breadcrumbs - The breadcrumb items
 * @param {object} props.illustration - The illustration to display in the hero
 * @param {string} props.searchLabel - The input search label
 * @param {string} props.title - The page title
 * @param {string} props.validationError - The validation error label
 * @returns {React.Component} The search header component
 */
const SearchHeader = ({ breadcrumbs, searchLabel, title, validationError, illustration }) => {
    const breakpoints = useBreakpoint();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const wasAutomaticallyCorrected = searchParams.get('a');

    const heroRef = React.useRef(null);
    const lastScrollY = React.useRef(0);

    const {
        applyCorrection,
        clearSearchFilters,
        didYouMean,
        filters,
        filterValue,
        searchValue,
        totalCount,
        triggerExecuteFilter,
    } = useSiteSearchContext();
    const state = useSiteSearchState();

    const [isHeroSticky, setIsHeroSticky] = React.useState(false);
    const [heroOffset, setHeroOffset] = React.useState(heroRef.current?.offsetHeight || 0);
    const [hasShadow, setHasShadow] = React.useState(false);

    const {
        hasQueryCorrection,
        queryCorrection: { correctedQuery },
    } = didYouMean;

    // Handle SearchHero behavior on scroll
    React.useEffect(() => {
        /**
         * Handle the scroll event
         * @returns {void}
         */
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // Update the search hero's offset
            if (heroRef.current) {
                setHeroOffset(heroRef.current.offsetHeight);
            }

            /**
             * Update component style depending on scroll direction
             * The hardcoded value is used to stabilize the scroll behavior
             * when the Typeahead dropdown is opened
             **/
            if (currentScrollY > lastScrollY.current + 10) {
                // Disable hero stickyness
                setIsHeroSticky(false);
                // Enable filters shadow
                setHasShadow(true);
            } else if (currentScrollY < lastScrollY.current - 10) {
                // Disable hero stickyness
                setIsHeroSticky(true);
            }

            // If scrolled to the top...
            if (currentScrollY === 0) {
                // Disable filters shadow
                setHasShadow(false);
            }

            lastScrollY.current = currentScrollY;
        };

        // Keep SearchHero height up-to-date
        const handleResize = () => {
            if (heroRef.current) {
                setHeroOffset(heroRef.current.offsetHeight);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <>
            {/* Search Hero */}
            <div
                className={classnames(
                    styles['search-header__hero'],
                    isHeroSticky ? styles['search-header__hero--sticky'] : '',
                )}
                style={{ '--search-hero-offset': `${-heroOffset}px` }}
            >
                <SearchHero
                    breadcrumbs={breadcrumbs}
                    illustration={illustration}
                    initialSearchValue={searchValue}
                    searchLabel={searchLabel}
                    title={title}
                    validationError={validationError}
                    ref={heroRef}
                />
            </div>

            {/* Additional Search Tools (Filters, Search Results Count) */}
            <ContentContainer
                className={classnames(
                    styles['search-header__tools'],
                    isHeroSticky ? styles['search-header__tools--vertical-shift'] : '',
                    hasShadow ? styles['search-header__tools--shadow'] : '',
                )}
                isFullWidth={!breakpoints?.medium}
                style={{ '--search-tools-offset': `${heroOffset}px` }}
            >
                {/* Did you mean...? */}
                <div className={styles['search-header__additional-content']}>
                    {state === 'results' && wasAutomaticallyCorrected && (
                        <>
                            <Text
                                tag={Text.TAG.P}
                                variant={Text.VARIANT.T2}
                                content="Showing results for"
                                className={styles['search-header__additional-content-text']}
                            />
                            <Text
                                tag={Text.TAG.P}
                                variant={Text.VARIANT.T2}
                                content={searchValue}
                                className={styles['search-header__additional-autocorrect-text']}
                            />
                        </>
                    )}
                    {state === 'results' && hasQueryCorrection && (
                        <>
                            <Text
                                tag={Text.TAG.P}
                                variant={Text.VARIANT.T2}
                                content="Did you mean?"
                                className={styles['search-header__additional-content-text']}
                            />
                            {correctedQuery && (
                                <Button
                                    buttonStyle={Button.STYLE.BREADCRUMB}
                                    label={correctedQuery}
                                    ariaLabel={`Search for ${correctedQuery}`}
                                    className={styles['search-header__additional-content-button']}
                                    onClick={applyCorrection}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Search Filters */}
                {state === 'results' && (
                    <SearchFilters
                        className={styles['search-header__filters']}
                        clearSearchFilters={clearSearchFilters}
                        filters={filters}
                        initialFilterValue={filterValue}
                        triggerExecuteFilter={triggerExecuteFilter}
                        searchValue={searchValue}
                    />
                )}

                {/* Search Results Count Label */}
                <div className={styles['search-header__additional-content']}>
                    {state === 'results' && (
                        <>
                            <Text
                                tag={Text.TAG.P}
                                variant={Text.VARIANT.T2}
                                content={`${totalCount} results`}
                                className={styles['search-header__additional-content-text']}
                            />
                            {searchValue && (
                                <>
                                    <Text
                                        tag={Text.TAG.P}
                                        variant={Text.VARIANT.T2}
                                        content={`for`}
                                        className={styles['search-header__additional-content-text']}
                                    />
                                    <Text
                                        tag={Text.TAG.P}
                                        variant={Text.VARIANT.T2B}
                                        content={`"${searchValue}"`}
                                        lineClamp={{ sm: 1, lg: 1 }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
            </ContentContainer>
        </>
    );
};

SearchHeader.propTypes = {
    breadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            href: PropTypes.string,
            title: PropTypes.string,
            csr: PropTypes.bool,
        }),
    ),
    illustration: PropTypes.shape({
        scene: PropTypes.string,
        alignment: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        excludes: PropTypes.arrayOf(PropTypes.string),
        autoplay: PropTypes.bool,
        loop: PropTypes.bool,
    }),
    searchLabel: PropTypes.string,
    title: PropTypes.string,
    validationError: PropTypes.string,
};

SearchHeader.defaultProps = {
    breadcrumbs: [],
    illustration: null,
    searchLabel: 'Search',
    title: 'Search',
    validationError: 'Please enter at least one search query',
};

export default SearchHeader;
